import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { DatePicker, Form, Modal, Select, Typography, message } from "antd";
import dayjs, { Dayjs } from "dayjs";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  EdgeUpdateRing,
  useCreateEdgeUpdateAssignmentMutation,
  useGetEdgeUpdateReleasesQuery,
} from "../../../redux/servers/serverAPI";
import { updateOpenModalUpdateServer } from "../../../redux/servers/serverSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// INTERFACES
import { RootState } from "../../../app/store";
// UTILS
import { errorMessage } from "./PendingEdgeEnrollments";
import { uuidToString } from "../../../utils/uuid";

const RINGS: EdgeUpdateRing[] = ["lab", "canary", "pilot", "general"];
const UPDATE_WINDOW_HOURS = 3;

interface UpdateFormValues {
  release_id: string;
  ring: EdgeUpdateRing;
  deadline: Dayjs;
}

function ServersUpdateModal() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const [form] = Form.useForm<UpdateFormValues>();

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { server, openModalUpdateServer } = useSelector((state: RootState) => state.server);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [token, setToken] = useState("");

  useEffect(() => {
    let active = true;
    if (openModalUpdateServer) {
      GETJwtToken()
        .then((value) => active && setToken(value))
        .catch(() => active && message.error(t("edgeInventory.authError")));
    }
    return () => {
      active = false;
    };
  }, [openModalUpdateServer, t]);

  const { data: releases, isLoading: isLoadingReleases } = useGetEdgeUpdateReleasesQuery(
    { token },
    { skip: !openModalUpdateServer || !token },
  );
  const [assign, { isLoading: isAssigning }] = useCreateEdgeUpdateAssignmentMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const versions: Record<string, string> = {};
  (releases?.data ?? []).forEach((release) => {
    versions[release.id] = release.version;
  });

  const onClickClose = () => {
    dispatch(updateOpenModalUpdateServer(false));
  };

  const onFinish = async (values: UpdateFormValues) => {
    try {
      await assign({
        token,
        body: {
          server_id: uuidToString(server.id),
          release_id: values.release_id,
          ring: values.ring,
          not_before: new Date().toISOString(),
          deadline: values.deadline.toISOString(),
        },
      }).unwrap();
      message.success(
        t("edgeServerUpdate.success", {
          name: String(server.name ?? ""),
          version: versions[values.release_id],
        }),
      );
      form.resetFields();
      onClickClose();
    } catch (requestError) {
      message.error(errorMessage(requestError, t("edgeServerUpdate.error")));
    }
  };

  // Preselect the newest selectable release once the list arrives.
  useEffect(() => {
    if (openModalUpdateServer && releases && !form.getFieldValue("release_id")) {
      const newest = releases.data.find((release) => !release.paused_at);
      if (newest) form.setFieldValue("release_id", newest.id);
    }
  }, [openModalUpdateServer, releases, form]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Modal
      title={t("edgeServerUpdate.title", { name: String(server.name ?? "") })}
      open={openModalUpdateServer}
      okText={t("edgeServerUpdate.assign")}
      confirmLoading={isAssigning}
      onOk={form.submit}
      onCancel={onClickClose}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary">
        {t("edgeServerUpdate.description")}
      </Typography.Paragraph>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ ring: RINGS[0], deadline: dayjs().add(UPDATE_WINDOW_HOURS, "hour") }}
      >
        <Form.Item
          name="release_id"
          label={t("edgeServerUpdate.release")}
          rules={[{ required: true, message: t("edgeServerUpdate.releaseRequired") }]}
        >
          <Select
            loading={isLoadingReleases}
            placeholder={t("edgeServerUpdate.releasePlaceholder")}
            options={(releases?.data ?? []).map((release) => ({
              value: release.id,
              disabled: Boolean(release.paused_at),
              label: `${release.version} · #${release.sequence}${
                release.paused_at ? ` · ${t("edgeServerUpdate.releasePaused")}` : ""
              }`,
            }))}
          />
        </Form.Item>
        <Form.Item name="ring" label={t("edgeServerUpdate.ring")}>
          <Select options={RINGS.map((ring) => ({ value: ring, label: ring }))} />
        </Form.Item>
        <Form.Item
          name="deadline"
          label={t("edgeServerUpdate.deadline")}
          rules={[{ required: true, message: t("edgeServerUpdate.deadlineRequired") }]}
        >
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default ServersUpdateModal;
